/**
 * The Audit Insights tab.
 *
 * Owns the fetch and the three states a reader can arrive in — loading, an audit with no
 * insights yet, and an audit with them — and hands the last to `ReadyInsights`.
 */
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { useCallback, useMemo, useState } from 'react';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Card } from '@amzn/stencil-react-components/card';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { AIIconWrapper } from '@amzn/stencil-react-components/ai';
import { IconStarSmall } from '@amzn/stencil-react-icons';

import { INSIGHT_STATUS, type OpenTaskRef } from 'src/types/starlight/AuditInsights';
import { useMessages } from 'src/v2/shared/i18n/useMessages';
import { htmlWithoutImages } from 'src/v2/shared/domain';
import { RefreshButton } from 'src/v2/shared/ui/RefreshButton';
import { AuditSummaryExpander } from 'src/v2/features/audit/components/AuditSummaryExpander';
import {
  AUDIT_SUMMARY_FIELD,
  type AuditSummaryAudit,
  type AuditSummaryFieldKey,
} from 'src/v2/features/audit/components/auditSummaryCellKeys';
import { useAuditInsightsQuery } from '../api';
import { INSIGHTS_BUNDLE, type AuditInsightsMessageKey } from '../translations';
import { ReadyInsights } from './ReadyInsights';
import {
  AuditInsightsFallback,
  type InsightQuestion,
  type ResponseBreakdown,
} from './AuditInsightsFallback';
import { incompleteReason } from './incompleteReason';
import { failedReason } from './failedReason';

/** Hoisted so an absent task list does not hand the view a new array each render. */
const NO_OPEN_TASKS: OpenTaskRef[] = [];

/**
 * The details this tab surfaces, in reading order. Name, status and score
 * live in the page header, so repeating them here would say the same thing
 * twice on one screen.
 */
/**
 * Columns the details grid uses. The grid otherwise fits as many cells as the
 * width allows, which in a desktop tab panel is eight — enough to lay all
 * seven fields out as one long row.
 */
const DETAIL_COLUMNS = 3;

const DETAIL_FIELDS: readonly AuditSummaryFieldKey[] = [
  AUDIT_SUMMARY_FIELD.template,
  AUDIT_SUMMARY_FIELD.templateId,
  AUDIT_SUMMARY_FIELD.assignees,
  AUDIT_SUMMARY_FIELD.createdBy,
  AUDIT_SUMMARY_FIELD.scheduledDate,
  AUDIT_SUMMARY_FIELD.startDate,
  AUDIT_SUMMARY_FIELD.expiryDate,
];

interface AuditInsightsTabProps {
  businessUnitId: string;
  auditId: string;
  /** Locale to serve the prose in. The server falls back to en-US when omitted. */
  language?: string;
  /**
   * The audit's questions, already resolved to the reader's language. Comes from the summary
   * the page has loaded, so the not-applicable view needs no second read.
   */
  questions: InsightQuestion[];
  /** The audit's own counts. Absent until the summary resolves. */
  breakdown?: ResponseBreakdown;
  /**
   * The audit itself, for the collapsed details panel above the card.
   * Absent until the summary resolves, and the panel is then omitted.
   */
  audit?: AuditSummaryAudit;
  /**
   * The audit's live status, resolved by the page.
   *
   * Separate from `audit` on purpose. That object is the search index's copy, which lags the
   * record while the two are syncing, and the page header reads the record. Deriving the
   * status here from `audit` instead would let the header call an audit cancelled while this
   * tab tells the reader it is still under way and its insights are coming.
   */
  auditStatus?: string;
}

/**
 * The Audit Insights tab: an AI-generated reading of a completed audit.
 *
 * Owns the four states the read can land in and nothing else — each section is its own
 * component. The score shown anywhere beneath here is the audit's own, served verbatim by
 * the API and never recomputed.
 */
export function AuditInsightsTab({
  businessUnitId,
  auditId,
  language,
  questions,
  breakdown,
  audit,
  auditStatus,
}: AuditInsightsTabProps): JSX.Element {
  const messages = useMessages<AuditInsightsMessageKey>(INSIGHTS_BUNDLE);
  const { t } = messages;

  // The audit's own counts where it has them, a count of none where it does not.
  //
  // Resolved one field at a time rather than falling back only when the whole object is
  // missing. `AuditMetrics` declares these required, but it is hand-written and Helios omits
  // a count it has not recorded — so an audit with nothing answered arrives carrying a
  // metrics object whose counts are absent. Read straight through, each one renders as
  // nothing at all, where the reader is owed a zero.
  //
  // Memoised so the not-started view receives a stable reference.
  const resolvedBreakdown = useMemo(
    () => ({
      passed: breakdown?.passed ?? 0,
      failed: breakdown?.failed ?? 0,
      na: breakdown?.na ?? 0,
      // A question count the audit never reported is still knowable: the questions are here.
      total: breakdown?.total ?? questions.length,
    }),
    [breakdown, questions.length],
  );

  // The insight names the questions it found but does not carry their wording, so the
  // summary's own copy is keyed by id for the detail views to join against.
  //
  // Images come out here, at the one point question wording enters this tab. A summary row
  // carries no `mediaResponses` map, so an image's src is still a media id that the browser
  // cannot load, and this is a plain transform of rows already in hand — there is nothing
  // here to resolve a media id against.
  //
  // To show the images, resolving has to move to where one question is open at a time: the
  // detail modal fetches that question with `useQuestionQuery` and calls
  // `replaceMediaIdsWithUrls` on its own copy of the wording, as
  // `features/task/components/QuestionInfoExpander` already does. Deleting this call is
  // then what turns the images back on.
  const questionTextById = useMemo(
    () =>
      new Map(
        questions.map((question) => [
          question.questionId,
          htmlWithoutImages(question.questionText),
        ]),
      ),
    [questions],
  );

  // `isError` is deliberately not read. React Query sets `status: 'error'` on a failed
  // fetch whether or not the entry already holds data (`query-core/query.js:385` spreads the
  // previous state), so a failed poll during a PENDING wait reports an error while the last
  // PENDING response is still in hand. Branching on it there would replace the informational
  // banner with the load error for one interval and then flip back — a red flash on the
  // path most readers take. What the reader is owed is the last state actually read, so the
  // render below turns on `data` alone.
  // No `enabled` guard on the coordinates, deliberately. `AuditSummaryPage` reads both out
  // of the query string and returns nothing when either is absent, so this tab cannot mount
  // without them. A guard here would also have to be rendered: a disabled query reports
  // `isLoading` false with no data, which is the shape the load error below turns on, so it
  // would show a failure for a read never attempted.
  const { data, isLoading, refetch } = useAuditInsightsQuery(
    language ? { businessUnitId, auditId, language } : { businessUnitId, auditId },
  );

  // Whether the reader asked for a re-read, not whether one is in flight.
  //
  // `isFetching` is the obvious source and is the wrong one: the tab polls every ten seconds
  // while PENDING, so it goes true on each cycle and would spin and disable the button for a
  // reader who never pressed it. This flag is what makes the spin mean "your press is being
  // served".
  //
  // It is not the guard against a second press. React Query collapses a refetch of a key it
  // is already fetching into the read in flight, so a double press costs no extra request;
  // disabling is feedback, not protection.
  const [isRefreshRequested, setIsRefreshRequested] = useState(false);

  // Lets a reader on PENDING re-read on demand.
  //
  // The automatic poll is bounded — see `MAX_PENDING_READS` — and generation is bounded too,
  // but by a longer window: the queue behind it delays the first attempt and then redelivers
  // a failed one several times, which can outlast the poll. This is what covers the gap, and
  // it has no bound of its own, so an insight that arrives after the poll has stopped is
  // still reachable without a page reload.
  //
  // `refetch` rather than a cache invalidation because there is one entry to re-read, and it
  // resolves to the same PENDING banner when nothing has changed. A rejection is caught and
  // dropped rather than handled: the read's own failure reaches the reader through the query's
  // state — see the note on `isError` below — so all this has left to do is stop spinning.
  const handleRefresh = useCallback(() => {
    setIsRefreshRequested(true);
    void refetch()
      .catch(() => undefined)
      .finally(() => setIsRefreshRequested(false));
  }, [refetch]);

  return (
    <Col gridGap="dimensions.spacing.300" width="100%" minWidth={0}>
      {audit && (
        <AuditSummaryExpander
          audit={audit}
          fields={DETAIL_FIELDS}
          defaultExpanded={false}
          columns={DETAIL_COLUMNS}
        />
      )}
      <InsightsCard
        title={t('cardTitle')}
        attribution={t('poweredBy')}
        badge={t('aiGenerated')}
      >
        {renderBody()}
      </InsightsCard>
    </Col>
  );

  function renderBody(): JSX.Element {
    if (isLoading) {
      return (
        <Row justifyContent="center" padding="dimensions.spacing.400">
          <Spinner aria-label={t('loading')} />
        </Row>
      );
    }

    // Nothing has been read yet, or the first read failed. Either way there is no state to
    // report. A read that failed *after* one succeeded keeps its response and renders on.
    if (!data) {
      return renderLoadError();
    }

    switch (data.status) {
      case INSIGHT_STATUS.READY:
        // The document is only guaranteed present on READY, so the sections read it from
        // here rather than re-deriving the status.
        return data.insights ? (
          <ReadyInsights
            businessUnitId={businessUnitId}
            insights={data.insights}
            openTasks={data.openTasks ?? NO_OPEN_TASKS}
            openTasksUnavailable={data.openTasksUnavailable ?? false}
            questionTextById={questionTextById}
            messages={messages}
          />
        ) : (
          renderLoadError()
        );

      case INSIGHT_STATUS.PENDING:
        return (
          <StateBanner
            type={MessageBannerType.Informational}
            title={t('pendingTitle')}
            body={t('pendingText')}
            // Only this state gets the button. A re-read is the whole action, so it is worth
            // offering exactly where re-reading can change the answer: FAILED is the row the
            // generator settled on and re-reading returns it unchanged, which would leave the
            // reader clicking a button that cannot work.
            action={
              <RefreshButton
                onRefresh={handleRefresh}
                isRefreshing={isRefreshRequested}
                label={t('pendingRefresh')}
              />
            }
          />
        );

      case INSIGHT_STATUS.FAILED:
        return (
          // The same body a non-completed audit gets, not a banner alone: the audit's record is
          // in hand either way, and withholding it left the reader whose insight will never
          // arrive with less than the reader whose audit is merely unfinished.
          //
          // `reason` chooses the wording but is never printed — it is the generator's own
          // vocabulary, which the read API documents as operator-facing. See `failedReason`.
          <AuditInsightsFallback
            banner={failedReason(data.reason)}
            breakdown={resolvedBreakdown}
            questions={questions}
            messages={messages}
          />
        );

      case INSIGHT_STATUS.NOT_APPLICABLE:
        return (
          <AuditInsightsFallback
            // The read API reports one NOT_APPLICABLE for every non-completed audit, so the
            // reason a reader is owed — still coming, or never — comes from the audit's
            // status. The page's resolved one, not `audit.status`: see the prop's note.
            banner={incompleteReason(auditStatus)}
            breakdown={resolvedBreakdown}
            questions={questions}
            messages={messages}
          />
        );

      // InsightStatus is a closed union but the wire is not. A status this
      // build has not learned reads as a failed load rather than leaving the
      // card with no body.
      default:
        return renderLoadError();
    }
  }

  /** The read failed, or resolved to a shape that cannot be rendered. */
  function renderLoadError(): JSX.Element {
    return (
      <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
        {t('loadError')}
      </MessageBanner>
    );
  }
}

/** Card chrome shared by every state: heading, attribution and the AI-generated badge. */
function InsightsCard({
  title,
  attribution,
  badge,
  children,
}: {
  title: string;
  attribution: string;
  badge: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    // The card owns no padding: the heading band is full-bleed, so it and the body
    // pad themselves.
    <Card padding="dimensions.spacing.none">
      <Col width="100%" minWidth={0}>
        <Row
          alignItems="center"
          justifyContent="space-between"
          gridGap="dimensions.spacing.200"
          padding="dimensions.spacing.300"
          backgroundColor="color.status.info-bg"
          flexWrap="wrap"
        >
          <Row alignItems="center" gridGap="dimensions.spacing.200" minWidth={0}>
            <AIIconWrapper>
              <IconStarSmall />
            </AIIconWrapper>
            <H3 fontWeight="bold">{title}</H3>
            <Text color="color.text.secondary" fontSize="T100">
              {attribution}
            </Text>
          </Row>
          <Badge type={BadgeType.Primary} value={badge} />
        </Row>
        <Col
          gridGap="dimensions.spacing.300"
          padding="dimensions.spacing.300"
          minWidth={0}
        >
          {children}
        </Col>
      </Col>
    </Card>
  );
}

/**
 * A titled banner for PENDING, the one state with no record worth showing beneath it — FAILED and
 * NOT_APPLICABLE render the audit's counts and questions through {@link AuditInsightsFallback}.
 *
 * `action` is an optional control beneath the body, for a state the reader can do something
 * about. `MessageBanner` has no action slot of its own, so it goes in the children below the
 * text, which is also the order it should be read in.
 */
function StateBanner({
  type,
  title,
  body,
  action,
}: {
  type: MessageBannerType;
  title: string;
  body: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <MessageBanner type={type} isDismissible={false} titleText={title}>
      {/* The body stays a bare text child so it keeps inheriting the banner's own
          typography; a `Text` here would bring its own colour and size instead. */}
      <Col gridGap="dimensions.spacing.200" alignItems="flex-start">
        {body}
        {action}
      </Col>
    </MessageBanner>
  );
}
